import type { PasswordRejection } from "../../shared/api-types";
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "../../shared/password-rules";
import type { Dictionary } from "./de";

const PASSWORD_TEXTS: Record<PasswordRejection, (dict: Dictionary) => string> = {
  password_too_short: (d) => d.password.tooShort(PASSWORD_MIN_LENGTH),
  password_too_long: (d) => d.password.tooLong(PASSWORD_MAX_LENGTH),
  password_personal: (d) => d.password.personal,
  password_weak: (d) => d.password.weak,
  password_compromised: (d) => d.password.compromised,
};

/**
 * Passwort-Richtlinien-Codes → Text. Nimmt beide Schreibweisen: `password_weak`
 * aus unseren Routen/der Live-Prüfung und `PASSWORD_WEAK` aus better-auth-Fehlern
 * (auch dessen eigenes PASSWORD_TOO_SHORT). null für alles andere.
 */
export function passwordErrorText(
  dict: Dictionary,
  code: string | undefined | null,
): string | null {
  const key = code?.toLowerCase() as PasswordRejection | undefined;
  return key && key in PASSWORD_TEXTS ? PASSWORD_TEXTS[key](dict) : null;
}

// Fehlercodes → Text. Bewusst ohne React: die Zuordnung ist reine Logik und
// wird einzeln getestet (Kopplung Worker-/Server-Codes ↔ Wörterbuch).

/** API-Fehlercodes (Server bzw. api.ts-Fallbacks) → lokalisierte Meldung; Unbekanntes wird roh gezeigt. */
export function apiError(dict: Dictionary, raw: string): string {
  if (raw.startsWith("http_")) return dict.apiError.http(Number(raw.slice(5)) || 0);
  switch (raw) {
    case "unauthorized":
      return dict.apiError.unauthorized;
    case "forbidden":
      return dict.apiError.forbidden;
    case "invalid_input":
      return dict.apiError.invalidInput;
    case "tour_not_found":
      return dict.apiError.tourNotFound;
    case "scene_not_found":
      return dict.apiError.sceneNotFound;
    case "hotspot_not_found":
      return dict.apiError.hotspotNotFound;
    case "project_not_found":
      return dict.apiError.projectNotFound;
    case "account_not_found":
      return dict.apiError.accountNotFound;
    case "no_account":
      return dict.apiError.noAccount;
    case "already_member":
      return dict.apiError.alreadyMember;
    case "invalid_role":
      return dict.apiError.invalidRole;
    case "invitation_invalid":
      return dict.apiError.invitationInvalid;
    case "invitation_expired":
      return dict.apiError.invitationExpired;
    case "email_mismatch":
      return dict.apiError.emailMismatch;
    case "user_exists":
      return dict.apiError.userExists;
    case "invalid_target_scene":
      return dict.apiError.invalidTargetScene;
    case "no_original":
      return dict.pipeline.noOriginal;
    case "unsupported_media_type":
      return dict.apiError.unsupportedMediaType;
    case "scene_processing":
      return dict.apiError.sceneProcessing;
    case "file_too_large":
      return dict.apiError.fileTooLarge;
    case "empty_upload":
      return dict.apiError.emptyUpload;
    case "upload_failed":
      return dict.apiError.uploadFailed;
    case "no_ready_scene":
      return dict.apiError.noReadyScene;
    case "no_previous_image":
      return dict.apiError.noPreviousImage;
    case "published_scene_not_ready":
      return dict.apiError.publishedSceneNotReady;
    case "scene_list_mismatch":
      return dict.apiError.sceneListMismatch;
    case "network_error":
      return dict.apiError.network;
    default:
      return passwordErrorText(dict, raw) ?? raw; // unbekannter Code bleibt sichtbar (Debughilfe)
  }
}

/** Pipeline-Fehlercodes (Worker) → lokalisierte Meldung; Unbekanntes wird roh gezeigt. */
export function pipelineError(dict: Dictionary, raw: string | null): string | null {
  if (!raw) return null;
  const [code, param = ""] = raw.split(":");
  switch (code) {
    case "unreadable":
      return dict.pipeline.unreadable;
    case "wrong_ratio":
      return dict.pipeline.wrongRatio(param);
    case "too_small":
      return dict.pipeline.tooSmall(param);
    case "no_original":
      return dict.pipeline.noOriginal;
    case "process_failed":
      return dict.pipeline.failed;
    default:
      return raw; // Alt-Daten mit Klartext bleiben lesbar
  }
}
